import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { BiData, BiContextProvidedValue, ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import OnScrollBottom, { DISTANCE_UNIT } from '@wix/da-shared-react/pkg/OnScrollBottom';
import useCanCreateSubfolder from '../../../hooks/useCanCreateSubfolder';
import { getSelectedFolder } from '../../../selectors/studio';
import CreateFolderCard from '../../_partials/CreateFolderCard';
import FolderCard from '../../_partials/FolderCard';
import { Props, THUMB_WIDTH, THUMB_HEIGHT } from './FolderListDesktop';

import s from './FolderListMobile.scss';

const THUMB_GAP = 16;

// Wrapper to decouple the measure hook from the desktop version
const FolderListMobile: FC<Props> = ({ streamId, withCreateButton, withFolderActions }) => {
  const { items, isFetching, hasMore, fetchNext } = useStream(streamId || '');
  const { ref, dimensions: measuredContainer } = useMeasureElementCookie(
    MeasuredCookieType.STASH_FOLDER_CARD,
    { cookieDim: 'width' }
  );

  const thumbWidth = measuredContainer?.width
    ? (measuredContainer.width - THUMB_GAP) / 2
    : THUMB_WIDTH;

  const selectedFolder = useSelector(getSelectedFolder);
  const canCreateFolder = useCanCreateSubfolder(selectedFolder);

  return (
    <BiLoggerContextProvider value={{ sectionname: 'subfolders' }}>
      <div className={s['root']} ref={ref}>
        <div className={s['expanded-grid']}>
          {withCreateButton && canCreateFolder && (
            <CreateFolderCard width={thumbWidth} height={THUMB_HEIGHT} folderType="gallery" />
          )}
          {items.map(folder => (
            <FolderCard
              key={`folder-${folder.folderId}`}
              folder={folder}
              width={thumbWidth}
              height={THUMB_HEIGHT}
              withActions={withFolderActions}
              biData={BiData<ItemClickBiEvent>({
                evid: 100,
                view: BiContextProvidedValue,
                component: BiContextProvidedValue,
                page_typeid: BiContextProvidedValue,
                page_itemid: BiContextProvidedValue,
                sectionname: BiContextProvidedValue,
                typeid: ResourceType.STASH_FOLDER,
                itemid: folder?.folderId,
              })}
            />
          ))}
        </div>

        {hasMore && !isFetching ? (
          <OnScrollBottom
            checkOnMount
            checkOnUpdate={hasMore}
            onScrolledToBottom={() => fetchNext()}
            distanceToBottom={100}
            distanceUnit={DISTANCE_UNIT.PX}
          />
        ) : null}
      </div>
    </BiLoggerContextProvider>
  );
};
FolderListMobile.displayName = 'FolderListMobile';

export default FolderListMobile;
